import { createEmitter } from "ts_utils";
import { Templates } from "../ui/Templates";
import { Student } from "./Student";
import { StudentManager } from "./StudentManager";
import { Services } from "../services/Services";
import { TeamDO } from "./TeamDO";
import { RdtoMessage } from "ts_utils";
import { addStudentDg } from "../ui/templates/addStudentTemplates";

/**Displays a dialog interface for adding a new student. Dispatches an event to
 * [onEvent] Stream. If a new [Student] object is created, it is passed to the
 * [onEvent] handler function.*/
export class AddStudentDialog {
	private _ui:HTMLDivElement;
	private _nameInput:HTMLInputElement;
	private _yesRbtn:HTMLInputElement;
	private _noRbtn:HTMLInputElement;
	private _codeInput:HTMLInputElement;
	private _infoDiv:HTMLDivElement;
	private _saveBtn:HTMLButtonElement;
	private _cancelBtn:HTMLButtonElement;
	
	private _team:TeamDO;
	
	public get studentEvent() { return this._studentEmitter.event; }
	private _studentEmitter = createEmitter<Student>();
	
	constructor(){
		this._init();
	}
	
	private _init():void {
		this._draw();
		this._addListeners();
	}
	
	private _draw():void {
		this._ui = Templates.createDiv(addStudentDg);
		this._nameInput = this._ui.querySelector(".studentdg-nameInput");
		this._codeInput = this._ui.querySelector(".studentdg-codeInput");
		this._infoDiv = this._ui.querySelector(".studentdg-info");
		this._saveBtn = this._ui.querySelector(".studentdg-saveBtn");
		this._cancelBtn = this._ui.querySelector(".studentdg-cancelBtn");
		
		let rbuttons = Array.from(this._ui.querySelectorAll(
			"input[name=codeQuestion]") as NodeListOf<HTMLInputElement>);
		this._yesRbtn = rbuttons.find((r) => r.value === "yes");
		this._noRbtn = rbuttons.find((r) => r.value === "no");
			
		this._codeInput.disabled = true;
		
		document.body.append(this._ui);
	}
	
	private _setMessage(message:string):void {
		this._infoDiv.innerHTML = message;
	}
	
	private _onCodeQuestionChange = (e:Event):void => {
		if (this._yesRbtn.checked){
			this._codeInput.disabled = false;
		}
		else {
			this._codeInput.disabled = true;
			this._codeInput.value = "";
		}		
	}
	
	private _onSaveBtnClick = async (e:MouseEvent):Promise<void> => {
		let name:string = this._nameInput.value;
		let code:string = (this._yesRbtn.checked)? this._codeInput.value : "";
		let success = await this._validateFields(name, code);
		if (!success){
			return;
		}
		
		let student = StudentManager.createStudent(name, this._team);
		StudentManager.saveToStorage(true);
		this._finish(student);
	}
	
	private async _validateFields(name:string, code:string):Promise<boolean> {
		if (name.length < 1){
			this._setMessage("Enter a name");
			return false;
		}
		if (StudentManager.students.filter((s) => s.name === name).length > 0){
			this._setMessage("A user with this name already exists.");
			return false;
		}
		
		if (!this._yesRbtn.checked && !this._noRbtn.checked){
			this._setMessage("Did your teacher give you a team code?");
			return false;
		}
		
		// student can create name with no team, but if a code is provided, it
		// must match a team.
		if (this._yesRbtn.checked){
			let rdto = await Services.getTeamByCode(code);
			if (rdto.success){
				this._team = TeamDO.fromTeamDto(rdto.data);
			}
			else {
				if (rdto.message === RdtoMessage.NOT_FOUND){
					this._setMessage("No team with this code exists.");
				}
				return false;
			}
		}
		
		return true;
	}
	
	private _onCancelBtnClick = (e:MouseEvent):void => {
		this._finish(null);
	}
	
	private _finish(student:Student):void {
		this._removeListeners();
		this._ui.remove();
		this._studentEmitter.emit(student);
	}
	
	private _addListeners():void {
		this._yesRbtn.addEventListener("change", this._onCodeQuestionChange);
		this._noRbtn.addEventListener("change", this._onCodeQuestionChange);
		this._saveBtn.addEventListener("click", this._onSaveBtnClick);
		this._cancelBtn.addEventListener("click", this._onCancelBtnClick);
	}
	
	private _removeListeners():void {
		this._yesRbtn.removeEventListener("change", this._onCodeQuestionChange);
		this._noRbtn.removeEventListener("change", this._onCodeQuestionChange);
		this._saveBtn.removeEventListener("click", this._onSaveBtnClick);
		this._cancelBtn.removeEventListener("click", this._onCancelBtnClick);
	}
}